<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { getAllocationColors, getCategoryIcon } from './allocationCategories';
import { isFullDay } from './scheduleUtils';
import { formatTime } from 'shared/helpers/formatTime';
import { resolveAllocationAccentColor } from 'dashboard/routes/dashboard/planning/events/useEventStageColors';

const props = defineProps({
  allocation: { type: Object, required: true },
  event: { type: Object, default: null },
  slots: { type: Array, default: () => [] },
  resources: { type: Array, default: () => [] },
  accentColor: { type: String, default: null },
  anchorRect: { type: Object, default: null },
  hoveredResourceId: { type: [String, Number], default: null },
  hoveredSlotIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['mouseenter', 'mouseleave']);

const GAP = 4;

const { t } = useI18n();

const accent = computed(
  () =>
    props.accentColor ||
    resolveAllocationAccentColor(props.allocation, props.event)
);

const findResourceName = (resources, resourceId) => {
  if (!resources || !resourceId) return null;
  for (const resource of resources) {
    if (String(resource.id) === String(resourceId)) return resource.name;
    const nested = findResourceName(
      resource.children || resource.sub_resources || [],
      resourceId
    );
    if (nested) return nested;
  }
  return null;
};

const slotRows = computed(() => {
  const hoveredIds = new Set(
    (props.hoveredSlotIds || []).map(id => String(id))
  );
  const hoveredResourceId = props.hoveredResourceId;

  return [...(props.slots || [])]
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
    .map(slot => {
      const isHoveredSlot = hoveredIds.size
        ? hoveredIds.has(String(slot.id))
        : hoveredResourceId != null &&
          String(slot.resource_id) === String(hoveredResourceId);

      return {
        id: slot.id,
        title: slot.title || t('PLANNING.SCHEDULER.UNTITLED_SLOT'),
        time: isFullDay(slot)
          ? t('PLANNING.SCHEDULER.ALL_DAY')
          : `${formatTime(slot.start_at)} - ${formatTime(slot.end_at)}`,
        location:
          findResourceName(props.resources, slot.resource_id) ||
          t('PLANNING.EVENTS_CARD.TBA'),
        color:
          slot.metadata?.color ||
          accent.value ||
          getAllocationColors(slot, props.allocation).color,
        icon: getCategoryIcon(slot.category),
        dimmed:
          hoveredIds.size > 0 || hoveredResourceId != null
            ? !isHoveredSlot
            : false,
        date: slot.start_at ? new Date(slot.start_at) : null,
      };
    });
});

const isMultiDay = computed(() => {
  const dates = new Set(
    slotRows.value
      .map(s => s.date)
      .filter(Boolean)
      .map(d => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`)
  );
  return dates.size > 1;
});

const groupedSlotRows = computed(() => {
  if (!isMultiDay.value) return null;
  const groups = [];
  const byKey = new Map();
  for (const slot of slotRows.value) {
    const d = slot.date;
    if (!d) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!byKey.has(key)) {
      const group = { key, date: d, slots: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).slots.push(slot);
  }
  return groups;
});

const formatDayHeading = date =>
  date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const title = computed(
  () =>
    props.event?.name ||
    props.allocation?.title ||
    t('PLANNING.SCHEDULER.UNTITLED_SLOT')
);

const eventLabel = computed(() => props.event?.event_label || null);

const location = computed(() => {
  const names = [
    ...new Set(slotRows.value.map(slot => slot.location).filter(Boolean)),
  ].filter(name => name !== t('PLANNING.EVENTS_CARD.TBA'));
  if (names.length) return names.join(', ');
  const allocated = props.event?.allocated_resources || [];
  if (allocated.length) {
    return allocated
      .map(resource => resource.name)
      .filter(Boolean)
      .join(', ');
  }
  return t('PLANNING.EVENTS_CARD.TBA');
});

const dateLabel = computed(() => {
  const start = props.allocation?.start_at
    ? new Date(props.allocation.start_at)
    : null;
  if (!start || Number.isNaN(start.getTime())) {
    return t('PLANNING.EVENTS_CARD.TBA');
  }
  return start.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
});

const timeLabel = computed(() => {
  if (!props.allocation?.start_at) return t('PLANNING.EVENTS_CARD.TBA');
  if (isFullDay(props.allocation)) return t('PLANNING.SCHEDULER.ALL_DAY');
  return formatTime(props.allocation.start_at);
});

const pax = computed(() => {
  const value =
    props.event?.pax ??
    props.event?.custom_attributes?.pax ??
    props.event?.metadata?.pax;
  if (value === null || value === undefined || value === '') return null;
  return value;
});

const organization = computed(
  () =>
    props.event?.customer?.name ||
    props.event?.custom_attributes?.organization ||
    null
);

const cardRef = ref(null);
const placedStyle = ref({});

const placeCard = () => {
  const rect = props.anchorRect;
  const el = cardRef.value;
  if (!rect) {
    placedStyle.value = {};
    return;
  }

  const width = el?.offsetWidth || 424;
  const height = el?.offsetHeight || 0;
  let left = rect.left;
  let top = rect.bottom + GAP;

  if (left + width > window.innerWidth - 8) {
    left = Math.max(8, rect.right - width);
  }
  if (height && top + height > window.innerHeight - 8) {
    top = Math.max(8, rect.top - height - GAP);
  }

  placedStyle.value = { left: `${left}px`, top: `${top}px` };
};

watch(
  () => [props.anchorRect, slotRows.value.length],
  async () => {
    await nextTick();
    placeCard();
  },
  { immediate: true }
);

onMounted(() => {
  nextTick(() => {
    requestAnimationFrame(placeCard);
  });
});
</script>

<template>
  <div
    ref="cardRef"
    class="fixed z-[80] flex max-h-[max(32rem,calc(100vh-2rem))] w-[26.5rem] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl border border-n-slate-6 bg-white dark:bg-n-solid-2 shadow-xl pointer-events-auto"
    :style="placedStyle"
    @mouseenter="emit('mouseenter')"
    @mouseleave="emit('mouseleave')"
  >
    <span
      class="pointer-events-none absolute left-2 top-2.5 bottom-2.5 w-1 rounded-full"
      :style="{ backgroundColor: accent }"
    />
    <div class="shrink-0 pl-6 pr-4 pt-4 pb-3">
      <div class="flex items-start justify-between gap-3">
        <div class="flex items-center gap-2 min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12 truncate mb-0">
            {{ title }}
          </h3>
          <span
            v-if="eventLabel"
            class="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-n-slate-3 outline outline-1 outline-n-slate-6 text-xs font-semibold text-n-slate-12 shrink-0 max-w-[9rem]"
          >
            <span
              class="size-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: eventLabel.color }"
            />
            <span class="truncate">{{ eventLabel.title }}</span>
          </span>
        </div>
      </div>

      <div class="mt-3 space-y-1.5 text-n-slate-11">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <div class="flex items-center gap-1.5">
            <Icon icon="i-lucide-map-pin" class="size-3.5 shrink-0" />
            <span class="text-sm">{{ location }}</span>
          </div>
          <div class="flex items-center gap-1.5">
            <Icon icon="i-lucide-calendar" class="size-3.5 shrink-0" />
            <span class="text-sm">{{ dateLabel }}</span>
          </div>
          <div class="flex items-center gap-1.5">
            <Icon icon="i-lucide-clock" class="size-3.5 shrink-0" />
            <span class="text-sm">{{ timeLabel }}</span>
          </div>
        </div>
        <div
          v-if="pax || organization"
          class="flex flex-wrap items-center gap-x-4 gap-y-1.5"
        >
          <div v-if="pax" class="flex items-center gap-1.5">
            <Icon icon="i-lucide-users" class="size-3.5 shrink-0" />
            <span class="text-sm">{{ pax }}</span>
          </div>
          <div v-if="organization" class="flex items-center gap-1.5">
            <Icon icon="i-lucide-handshake" class="size-3.5 shrink-0" />
            <span class="text-sm">{{ organization }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="slotRows.length"
      class="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-n-weak pl-6 pr-4 py-2"
    >
      <!-- Multi-day: grouped by date -->
      <template v-if="groupedSlotRows">
        <div
          v-for="group in groupedSlotRows"
          :key="group.key"
          class="mb-2 last:mb-0"
        >
          <p
            class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide pt-1 pb-0.5 mb-0"
          >
            {{ formatDayHeading(group.date) }}
          </p>
          <div class="space-y-0.5">
            <div
              v-for="slot in group.slots"
              :key="slot.id"
              class="flex items-start gap-2.5 py-1.5 transition-opacity duration-150"
              :class="{ 'opacity-30': slot.dimmed }"
            >
              <span
                class="w-1 self-stretch rounded-full shrink-0 min-h-8"
                :style="{ backgroundColor: slot.color }"
              />
              <Icon
                v-if="slot.icon"
                :icon="`i-lucide-${slot.icon}`"
                class="size-4 text-n-slate-11 shrink-0 mt-0.5"
              />
              <div class="min-w-0 flex-1">
                <div class="flex items-center justify-between gap-3">
                  <p
                    class="text-sm font-semibold text-n-slate-12 truncate mb-0"
                  >
                    {{ slot.title }}
                  </p>
                  <span class="text-sm text-n-slate-11 shrink-0 text-right">
                    {{ slot.location }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-11 mb-0">{{ slot.time }}</p>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Single-day: flat list -->
      <div v-else class="space-y-0.5">
        <div
          v-for="slot in slotRows"
          :key="slot.id"
          class="flex items-start gap-2.5 py-1.5 transition-opacity duration-150"
          :class="{ 'opacity-30': slot.dimmed }"
        >
          <span
            class="w-1 self-stretch rounded-full shrink-0 min-h-8"
            :style="{ backgroundColor: slot.color }"
          />
          <Icon
            v-if="slot.icon"
            :icon="`i-lucide-${slot.icon}`"
            class="size-4 text-n-slate-11 shrink-0 mt-0.5"
          />
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-3">
              <p class="text-sm font-semibold text-n-slate-12 truncate mb-0">
                {{ slot.title }}
              </p>
              <span class="text-sm text-n-slate-11 shrink-0 text-right">
                {{ slot.location }}
              </span>
            </div>
            <p class="text-xs text-n-slate-11 mb-0">{{ slot.time }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
